import { NgModule } from '@angular/core';
import { Route, RouterModule, Routes } from '@angular/router';
import { DRAFT_TABS } from './drafts/draft-tabs';
import { unsavedChangesGuard } from './shared/draft-editor/unsaved-changes.guard';
import { AppComponent } from './app.component';
import { AuthGuard } from '../app/shared/services/auth-guard'
import { HomeComponent } from './home/home.component';
import { PaymentRequestComponent } from './payment-request/payment-request.component';
import { ExpectFundComponent } from './expect-fund/expect-fund.component';
import { PaymentRequestApprovalComponent } from './payment-request-approval/payment-request-approval.component';
import { ViewPaymentRequestComponent } from './view-payment-request/view-payment-request.component';
import { InvalidJurisdictionComponent } from './invalid-jurisdiction/invalid-jurisdiction.component';
import { NoAccessToViewComponent } from './no-access-to-view/no-access-to-view.component';
import { AuditLogComponent } from './audit-log/audit-log.component';
import { TransferFundsComponent } from './transfer-funds/transfer-funds.component';
import { JournalRequestComponent } from './journal-request/journal-request.component';
import { UserRequestsComponent } from './user/user-requests/user-requests.component';
import { UserPaymentHistoryComponent } from './user/user-payment-history/user-payment-history.component';
import { UserTransferHistoryComponent } from './user/user-transfer-history/user-transfer-history.component';
import { UserExpectHistoryComponent } from './user/user-expect-history/user-expect-history.component';
import { UserJournalHistoryComponent } from './user/user-journal-history/user-journal-history.component';
import { CompletedRequestsComponent } from './completed/completed-requests/completed-requests.component';
import { ViewAuditComponent } from './view-audit/view-audit.component';
import { DisbursementComponent } from './disbursement/disbursement.component';
import { ViewExpectFundComponent } from './view-expect-fund/view-expect-fund.component';
import { ViewTransferFundRequestComponent } from './view-transfer-fund-request/view-transfer-fund-request.component';
import { ViewJournalRequestComponent } from './view-journal-request/view-journal-request.component';

const routes: Routes = [
    {
        path: "",
        pathMatch: "full",
        redirectTo: 'payment-request'
    },
    {
        path: 'home',
        component: HomeComponent,
        canActivate: [AuthGuard]
    },
    {
        path: 'home/:matter',
        component: HomeComponent,
        canActivate: [AuthGuard]
    },
    {
        path: 'home/:client/:matter',
        component: HomeComponent,
        canActivate: [AuthGuard]
    },
    {
        path: 'home/:client/:matter/:userEmail',
        component: HomeComponent,
        canActivate: [AuthGuard]
    },
    {
        path: 'payment-request',
        component: PaymentRequestComponent,
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard]
    },
    {
        path: 'payment-request/draft/:draftId',
        component: PaymentRequestComponent,
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard]
    },
    {
        path: 'user-payment-request/:id',
        component: PaymentRequestComponent,
        canActivate: [AuthGuard]
    },
    {
        path: 'expect-fund',
        component: ExpectFundComponent,
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard]
    },
    {
        path: 'expect-fund/draft/:draftId',
        component: ExpectFundComponent,
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard]
    },
    {
        path: 'journal-request/draft/:draftId',
        component: JournalRequestComponent,
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard]
    },
    {
        path: 'journal-request',
        component: JournalRequestComponent,
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard]
    },
    {
        path: 'disbursement',
        component: DisbursementComponent,
        canActivate: [AuthGuard]
    },
    {
        path: 'approvals',
        component: PaymentRequestApprovalComponent,
        canActivate: [AuthGuard]
    },
    {
        path: 'approvals/payment-request/:id',
        component: ViewPaymentRequestComponent,
        canActivate: [AuthGuard]
    },
    {
        path: 'approvals/expect-funds-request/:id',
        component: ViewExpectFundComponent,
        canActivate: [AuthGuard]
    },
    {
        path: 'approvals/transfer-funds-request/:id',
        component: ViewTransferFundRequestComponent,
        canActivate: [AuthGuard]
    },
    {
        path: 'approvals/journal-request/:id',
        component: ViewJournalRequestComponent,
        canActivate: [AuthGuard]
    },
    {
        path: 'transfer-funds-request/draft/:draftId',
        component: TransferFundsComponent,
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard]
    },
    {
        path: 'transfer-funds-request',
        component: TransferFundsComponent,
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard]
    },
    {
        path: 'unsupported',
        component: InvalidJurisdictionComponent,
    },
    {
        path: 'unauthorised',
        component: InvalidJurisdictionComponent,
    },
    {
        path: 'audit',
        component: AuditLogComponent,
        canActivate: [AuthGuard]
    },
    {
        path: 'audit/:type/:id',
        component: ViewAuditComponent,
        canActivate: [AuthGuard]
    },
    {
        path: 'no-permissions',
        component: NoAccessToViewComponent,
        canActivate: [AuthGuard]
    },
    {
        path: 'user-requests',
        component: UserRequestsComponent,
        canActivate: [AuthGuard],
        children: [
            { path: 'payment-requests', component: UserPaymentHistoryComponent },
            { path: 'expect-funds', component: UserExpectHistoryComponent },
            { path: 'transfer-request', component: UserTransferHistoryComponent },
            { path: 'journal-requests', component: UserJournalHistoryComponent },
            { path: '', redirectTo: 'payment-requests', pathMatch: 'full' }
        ]
    },
    {
        path: 'user/payment-requests',
        component: UserPaymentHistoryComponent,
        canActivate: [AuthGuard]
    },
    {
        path: 'user/expect-funds',
        component: UserExpectHistoryComponent,
        canActivate: [AuthGuard]
    },
    {
        path: 'user/journal-requests',
        component: UserJournalHistoryComponent,
        canActivate: [AuthGuard]
    },
    {
        path: 'user/transfer-request',
        component: UserTransferHistoryComponent,
        canActivate: [AuthGuard]
    },
    {
        path: 'completed-requests',
        component: CompletedRequestsComponent,
        canActivate: [AuthGuard],
        children: [
            { path: 'payment-completed-requests', component: UserPaymentHistoryComponent },
            { path: 'expect-fund-completed', component: UserExpectHistoryComponent },
            { path: 'transfer-completed-requests', component: UserTransferHistoryComponent },
            { path: 'journal-completed-requests', component: UserJournalHistoryComponent },
            { path: '', redirectTo: 'payment-completed-requests', pathMatch: 'full' }
        ]
    },
    {
        // The base path contains "-request" on purpose: the nav menu's landing redirect treats URLs containing it as request pages.
        path: 'draft-requests',
        loadComponent: () => import('./drafts/draft-requests/draft-requests.component').then(m => m.DraftRequestsComponent),
        canActivate: [AuthGuard],
        children: [
            ...DRAFT_TABS.map((tab): Route => ({
                path: tab.path,
                loadComponent: () => import('./drafts/drafts-grid/drafts-grid.component').then(m => m.DraftsGridComponent),
                data: { requestType: tab.requestType }
            })),
            { path: '', redirectTo: DRAFT_TABS[0].path, pathMatch: 'full' }
        ]
    },
    {
        path: 'completed/payment-completed-requests',
        component: UserPaymentHistoryComponent,
        canActivate: [AuthGuard]
    },
    {
        path: 'completed/expect-fund-completed',
        component: UserExpectHistoryComponent,
        canActivate: [AuthGuard]
    },
    {
        path: 'completed/transfer-completed-requests',
        component: UserTransferHistoryComponent,
        canActivate: [AuthGuard]
    },
    {
        path: 'completed/journal-completed-requests',
        component: UserJournalHistoryComponent,
        canActivate: [AuthGuard]
    },
    { path: '**', component: AppComponent }
];

@NgModule({
    imports: [RouterModule.forRoot(routes)],
    exports: [RouterModule]
})
export class AppRoutingModule { }
